import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  path?: string;
  noSitelinks?: boolean;
  noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({ title, description, keywords, canonical, path, noSitelinks = false, noIndex = false }) => {
  const location = useLocation();
  const { language } = useLanguage();
  const baseUrl = 'https://www.grenzexpert.com';
  const canonicalUrl = canonical || `${baseUrl}${path || location.pathname}`;
  
  useEffect(() => {
    // Update document title - always use the same format
    document.title = title ? `GrenzExpert | ${title}` : 'GrenzExpert';
    
    // Robots meta — noindex for non-homepage pages
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
    }
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Google sitelinks search box control
    if (noSitelinks) {
      let googlebot = document.querySelector('meta[name="googlebot"]');
      if (!googlebot) {
        googlebot = document.createElement('meta');
        googlebot.setAttribute('name', 'googlebot');
        document.head.appendChild(googlebot);
      }
      googlebot.setAttribute('content', 'nosnippet');
    } else {
      // Remove googlebot meta if exists and not needed
      const googlebot = document.querySelector('meta[name="googlebot"]');
      if (googlebot) {
        googlebot.remove();
      }
    }
    
    // Canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;
    
    // Hreflang tags for bilingual SEO
    const currentPath = path || location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(fr|de)/, '');
    
    // Remove existing hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    
    // Add hreflang for French
    const hreflangFr = document.createElement('link');
    hreflangFr.setAttribute('rel', 'alternate');
    hreflangFr.setAttribute('hreflang', 'fr');
    hreflangFr.setAttribute('href', `${baseUrl}/fr${pathWithoutLang}`);
    document.head.appendChild(hreflangFr);
    
    // Add hreflang for German
    const hreflangDe = document.createElement('link');
    hreflangDe.setAttribute('rel', 'alternate');
    hreflangDe.setAttribute('hreflang', 'de');
    hreflangDe.setAttribute('href', `${baseUrl}/de${pathWithoutLang}`);
    document.head.appendChild(hreflangDe);
    
    // Add x-default for language selector
    const hreflangDefault = document.createElement('link');
    hreflangDefault.setAttribute('rel', 'alternate');
    hreflangDefault.setAttribute('hreflang', 'x-default');
    hreflangDefault.setAttribute('href', `${baseUrl}/fr${pathWithoutLang}`);
    document.head.appendChild(hreflangDefault);
    
    // Update html lang attribute
    document.documentElement.lang = language;
    
    // Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    
    if (ogTitle) ogTitle.setAttribute('content', `${title} | GrenzExpert`);
    if (ogDescription) ogDescription.setAttribute('content', description);
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    if (ogLocale) ogLocale.setAttribute('content', language === 'de' ? 'de_DE' : 'fr_FR');
    
    // Twitter
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    
    if (twitterTitle) twitterTitle.setAttribute('content', `${title} | GrenzExpert`);
    if (twitterDescription) twitterDescription.setAttribute('content', description);
    if (twitterUrl) twitterUrl.setAttribute('content', canonicalUrl);
    
  }, [title, description, keywords, canonicalUrl, language, location.pathname, path, noSitelinks, noIndex]);
  
  return null;
};

export default SEO;
